/* eslint-disable */
import { useContext, useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  BagXIcon,
  HeadsetIcon,
  Home,
  Insurance,
  PeopleIcon,
  Plus,
  QrCodeIcon,
  ReceiptOutline,
  TooltipQuestionIcon,
} from "../../common/icons";
import { LocalizationContext } from "../../App";
import { cn } from "../../common/helpers/formatters";
import { PolicyStatus } from "../../models/enum/PolicyStatus";
import { useGetPoliciesWithAvailableStatusTransitionsQuery } from "../../hooks/useGetPoliciesWithAvailableStatusTransitionsQuery";
import { useGetPoliciesAvailableForClaimCreationQuery } from "../../store/claimSlice";
import { ProductContext } from "../../context/ProductContext";
import { useAppSelector } from "../../store/store";

type ActionMenuProps = {
  expanded: boolean;
  changeRoute: (route: string, state?: any) => void;
};

const ActionMenu = ({ expanded, changeRoute }: ActionMenuProps) => {
  const l10n = useContext(LocalizationContext);
  const isLoggedIn = useAppSelector((state) => state.auth.isUserLogged);
  const { di } = useContext(ProductContext);
  const { data: policies } = useGetPoliciesWithAvailableStatusTransitionsQuery();
  const { data: policiesForClaimCreation = [] } = useGetPoliciesAvailableForClaimCreationQuery(
    undefined,
    { skip: !isLoggedIn }
  );
  const activeDiPolicies =
    policies?.filter(
      (policy) => policy.productTypeId === di && policy.statusId === PolicyStatus.Policy
    ) ?? [];
  const cancellablePolicies =
    policies?.filter(
      (policy) => policy.statusId === PolicyStatus.Policy && !!policy.termination
    ) ?? [];

  return (
    <nav className={`action-menu ${!expanded ? "hide" : "show"}`}>
      <div className="w-full flex justify-center">
        <div className="w-full max-w-[800px]">
          {cancellablePolicies.length > 0 && (
            <button
              className="action-menu-item"
              onClick={() => {
                changeRoute("insurance/cancel", { cancellablePolicies });
              }}
              type="button"
            >
              <BagXIcon className="action-menu-item-icon" />
              <span className="action-menu-item-title">{l10n.menu.insuranceCancellation}</span>
            </button>
          )}
          <button onClick={() => changeRoute("/broker")} className="action-menu-item">
            <HeadsetIcon className="action-menu-item-icon" />
            <span className="action-menu-item-title">{l10n.menu.contactRM}</span>
          </button>
          {policiesForClaimCreation.length > 0 && (
            <button onClick={() => changeRoute("/claim/submit")} className="action-menu-item">
              <QrCodeIcon className="action-menu-item-icon" />
              <span className="action-menu-item-title">{l10n.menu.reportClaim}</span>
            </button>
          )}
          {activeDiPolicies.length > 0 && (
            <button
              onClick={() =>
                changeRoute("/insurance/order-of-beneficiaries", {
                  policyUId: activeDiPolicies[0]?.uniqueIdentifier,
                  productVersionCode: activeDiPolicies[0]?.productVersionCode,
                })
              }
              className="action-menu-item"
            >
              <PeopleIcon className="action-menu-item-icon" />
              <span className="action-menu-item-title">{l10n.menu.orderOfBeneficiaries}</span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export type FooterProps = {
  show?: boolean;
};

const Footer = ({ show = true }: FooterProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(true);
  const [showActionButton, setShowActionButton] = useState(true);

  useEffect(() => {
    if (["/products"].includes(location.pathname)) {
      setShowMenu(false);
      setShowActionButton(false);
    } else {
      setShowMenu(true);
      setShowActionButton(true);
    }
  }, [location]);

  // Any navigation shall collapse the action menu
  useEffect(() => {
    if (expanded) setExpanded(false);
  }, [location.pathname]);

  const changeRoute = (route: string, state?: any) => {
    navigate(route, { state: state });
  };

  return (
    <>
      <ActionMenu expanded={expanded} changeRoute={changeRoute} />

      <button
        aria-label="Toggle action menu"
        className={cn("btn-action-menu", !showActionButton && "hidden", expanded && "expanded")}
        onClick={() => setExpanded(!expanded)}
      >
        <Plus className={"w-8 h-8 stroke-btn-action-menu"} />
      </button>

      <nav className={cn("footer", !(show && showMenu) && "hidden")}>
        <div className="h-footer flex justify-center">
          <div className="w-full max-w-[800px] h-footer flex items-center">
            <div className={`w-full inline-flex justify-between ${showMenu ? "" : "hidden"}`}>
              <div onClick={() => changeRoute("/insurance/installments")}>
                <ReceiptOutline
                  className={cn(
                    "footer-icon",
                    location.pathname === "/insurance/installments" && "selected"
                  )}
                />
              </div>
              <div onClick={() => changeRoute("/")}>
                <Home className={cn("footer-icon", location.pathname === "/" && "selected")} />
              </div>
              <div onClick={() => changeRoute("/insurance")}>
                <Insurance
                  className={cn("footer-icon", location.pathname === "/insurance" && "selected")}
                />
              </div>
              <div onClick={() => changeRoute("/faq")}>
                <TooltipQuestionIcon
                  className={cn("footer-icon", location.pathname === "/faq" && "selected")}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Footer;
